/**
 * Account-scoped UI prefs (theme + "don't show again"). Origin localStorage
 * is empty after a domain switch and can be session-only under bounce-tracking;
 * `user_metadata.bb_ui` follows the signed-in user.
 */
import { supabase } from "@/lib/supabase";

export const BB_UI_PREFS_HYDRATED_EVENT = "bb-ui-prefs-hydrated";
export const BB_THEME_STORAGE_KEY = "bb-theme";

export type UiThemePreference = "light" | "dark" | "system" | "special";

export type UserUiPrefs = {
  theme?: UiThemePreference;
  helpDismissed: Record<string, number>;
  inlineHelpDismissed: Record<string, true>;
};

const VALID_THEMES = new Set<UiThemePreference>(["light", "dark", "system", "special"]);

export function emptyUserUiPrefs(): UserUiPrefs {
  return { helpDismissed: {}, inlineHelpDismissed: {} };
}

let cached: UserUiPrefs = emptyUserUiPrefs();
let writeChain: Promise<void> = Promise.resolve();

export function parseUserUiPrefs(raw: unknown): UserUiPrefs {
  const out = emptyUserUiPrefs();
  if (!raw || typeof raw !== "object") return out;
  const rec = raw as Record<string, unknown>;
  if (typeof rec.theme === "string" && VALID_THEMES.has(rec.theme as UiThemePreference)) {
    out.theme = rec.theme as UiThemePreference;
  }
  if (rec.helpDismissed && typeof rec.helpDismissed === "object") {
    for (const [k, v] of Object.entries(rec.helpDismissed as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v) && v > 0) out.helpDismissed[k] = v;
    }
  }
  if (rec.inlineHelpDismissed && typeof rec.inlineHelpDismissed === "object") {
    for (const [k, v] of Object.entries(rec.inlineHelpDismissed as Record<string, unknown>)) {
      if (v) out.inlineHelpDismissed[k] = true;
    }
  }
  return out;
}

export function mergeUserUiPrefs(base: UserUiPrefs, patch: Partial<UserUiPrefs>): UserUiPrefs {
  const helpDismissed = { ...base.helpDismissed };
  if (patch.helpDismissed) {
    for (const [k, v] of Object.entries(patch.helpDismissed)) {
      helpDismissed[k] = Math.max(helpDismissed[k] ?? 0, v);
    }
  }
  const inlineHelpDismissed = { ...base.inlineHelpDismissed };
  if (patch.inlineHelpDismissed) {
    for (const k of Object.keys(patch.inlineHelpDismissed)) {
      if (patch.inlineHelpDismissed[k]) inlineHelpDismissed[k] = true;
    }
  }
  return {
    theme: patch.theme ?? base.theme,
    helpDismissed,
    inlineHelpDismissed,
  };
}

export function notifyUiPrefsHydrated(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(BB_UI_PREFS_HYDRATED_EVENT));
}

export function patchUserUiPrefs(patch: Partial<UserUiPrefs>): Promise<void> {
  writeChain = writeChain.then(() => patchUserUiPrefsNow(patch)).catch((err) => {
    console.warn("[ui-prefs] patch failed", err);
  });
  return writeChain;
}

async function patchUserUiPrefsNow(patch: Partial<UserUiPrefs>): Promise<void> {
  if (!supabase) return;
  const { data } = await supabase.auth.getSession();
  const user = data.session?.user;
  if (!user) return;
  const fromUser = parseUserUiPrefs(
    (user.user_metadata as Record<string, unknown> | undefined)?.bb_ui,
  );
  cached = mergeUserUiPrefs(mergeUserUiPrefs(fromUser, cached), patch);
  const { error } = await supabase.auth.updateUser({ data: { bb_ui: cached } });
  if (error) console.warn("[ui-prefs] updateUser failed", error.message);
}

export function rememberCachedUiPrefs(prefs: UserUiPrefs): void {
  cached = mergeUserUiPrefs(cached, prefs);
}
